import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  APIProvider,
  Map,
  AdvancedMarker,
  Pin,
  InfoWindow,
} from "@vis.gl/react-google-maps";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, List, Map as MapIcon, Star, Navigation, ExternalLink, Phone, ChevronDown, Sparkles } from "lucide-react";
import SearchResultsCard from "../SearchResultsCard/SearchResultsCard";
import MapSearchCard from "../MapSearchCard/MapSearchCard";
import { Button } from "../../../components/ui/button";
import { selectBlockedUserIds } from "../../../slices/blocksSlice";
import { selectEffectiveCoordinates } from "../../../slices/locationSlice";
import { navigateToAISearch } from "../../../utils/searchActions";
import { useAiConsent } from "../../../components/Consent/AiConsentProvider";
import { getDistanceUnitForCoordinates } from "../../../utils/distanceUnit";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_API;

const SearchResults = ({ data, category, title }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState("distance");
  const [hasUserSorted, setHasUserSorted] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [infoWindowVisible, setInfoWindowVisible] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);
  const markersRef = useRef({});
  const effectiveCoordinates = useSelector(selectEffectiveCoordinates);
  const [distanceUnit, setDistanceUnit] = useState(() => {
    const saved = localStorage.getItem('distanceUnit');
    if (saved === 'mi' || saved === 'km') return saved;
    return getDistanceUnitForCoordinates(effectiveCoordinates);
  });
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const unitDropdownRef = useRef(null);
  const { ensureAiConsent } = useAiConsent();

  const handleDistanceUnitChange = (unit) => {
    setDistanceUnit(unit);
    localStorage.setItem('distanceUnit', unit);
    setShowUnitDropdown(false);
  };

  const handleBackToHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleAskAI = useCallback(async () => {
    const consented = await ensureAiConsent();
    if (!consented) return;
    const seed = title || category || '';
    navigateToAISearch(navigate, seed);
  }, [ensureAiConsent, navigate, title, category]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (unitDropdownRef.current && !unitDropdownRef.current.contains(event.target)) {
        setShowUnitDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isMobile = window.innerWidth < 480;

  const userEffectiveCoordinates = useSelector((state) => state.location.temporaryCoordinates);
  const userCoordinates = useSelector((state) => state.location.coordinates);

  const blockedIds = useSelector(selectBlockedUserIds);
  const visibleData = useMemo(
    () =>
      (data || []).filter((item) => {
        if (!item?.userId) return true;
        return !blockedIds.includes(item.userId);
      }),
    [data, blockedIds]
  );

  const defaultCenter =
    userEffectiveCoordinates?.length === 2
      ? { lat: userEffectiveCoordinates[1], lng: userEffectiveCoordinates[0] }
      : { lat: userCoordinates[1], lng: userCoordinates[0] };

  useEffect(() => {
    if (selectedPlace && mapInstance) {
      const newCenter = {
        lat: selectedPlace.coordinates[1],
        lng: selectedPlace.coordinates[0],
      };
      setMapCenter(newCenter);
      mapInstance.panTo(newCenter);
    }
  }, [selectedPlace, mapInstance]);

  const handleMarkerClick = (place) => {
    if (selectedPlace?.id === place.id) return;
    
    setInfoWindowVisible(false);
    setTimeout(() => {
      setSelectedPlace(place);
      setInfoWindowVisible(true);
    }, 50);
  };

  const handleMapClick = () => {
    setSelectedPlace(null);
    setInfoWindowVisible(false);
  };

  if (!data || visibleData.length === 0) {
    return (
      <div className="min-h-screen bg-background pt-16">
        <div className="bg-white/80 backdrop-blur-sm px-3 sm:px-4 py-3 sm:py-4 border-b border-border sticky top-16 z-10">
          <div className="max-w-4xl mx-auto flex items-center gap-2 sm:gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackToHome}
              className="min-w-[40px] min-h-[40px] sm:min-w-[44px] sm:min-h-[44px]"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
            <h1 className="text-base sm:text-xl font-bold text-foreground flex-1 min-w-0 truncate">{title}</h1>
            <button
              type="button"
              onClick={handleAskAI}
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 hover:bg-primary/20
                       text-primary px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm font-medium
                       transition-colors flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-primary/40"
              aria-label={`${t('ask_ai', 'Ask AI')} ${t('about', 'about')} ${title}`}
            >
              <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="whitespace-nowrap">{t('ask_ai', 'Ask AI')}</span>
            </button>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center min-h-[50vh] px-4 gap-3 text-center">
          <p className="text-xs sm:text-sm text-muted-foreground">{t('no_results_found')}</p>
          <button
            type="button"
            onClick={handleAskAI}
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground
                     hover:bg-primary/90 px-4 py-2 text-sm font-medium transition-colors
                     focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-2"
          >
            <Sparkles className="w-4 h-4" />
            {t('ask_ai_about_x', 'Ask AI about')} {title}
          </button>
        </div>
      </div>
    );
  }

  const sortedData = hasUserSorted
    ? [...visibleData].sort((a, b) => {
        if (sortBy === "rating") return (b.rating || 0) - (a.rating || 0);
        return (a.distance || 0) - (b.distance || 0);
      })
    : visibleData;

  const validPlaces = visibleData.filter(
    (place) =>
      place.coordinates &&
      place.coordinates[0] &&
      place.coordinates[1] &&
      !isNaN(place.coordinates[1]) &&
      !isNaN(place.coordinates[0])
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-white px-3 sm:px-4 py-3 sm:py-4 border-b border-border sticky top-0 z-20">
        <div className="max-w-4xl mx-auto">
          {/* Title */}
          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackToHome}
              className="min-w-[36px] min-h-[36px] sm:min-w-[44px] sm:min-h-[44px]"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
            <div className="flex-1 min-w-0">
              <h1 className="text-base sm:text-lg md:text-xl font-bold text-foreground truncate">{title}</h1>
              <p className="text-[10px] sm:text-xs text-muted-foreground">
                {visibleData.length} results · {t('powered_by_google', 'Powered by Google Places API')}
              </p>
            </div>
            <button
              type="button"
              onClick={handleAskAI}
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 hover:bg-primary/20
                       text-primary px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm font-medium
                       transition-colors flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-primary/40"
              aria-label={`${t('ask_ai', 'Ask AI')} ${t('about', 'about')} ${title}`}
            >
              <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="whitespace-nowrap">{t('ask_ai', 'Ask AI')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sort Options */}
      <div className="px-3 sm:px-4 py-2.5 sm:py-3">
        <div className="max-w-2xl mx-auto">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <span className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {t('sort_by', 'Sort by')}
              </span>
              {!hasUserSorted && sortBy === 'distance' && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[9px] sm:text-[10px] font-medium text-primary"
                  title={t(
                    'ai_rerank_tooltip',
                    'Results re-ordered by AI to better match this category. Click Distance or Rating to sort manually.',
                  )}
                >
                  <Sparkles className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                  {t('sorted_by_ai_relevance', 'Sorted by AI relevance')}
                </span>
              )}
            </div>
            <div className="flex items-center justify-between">
              {/* Sort Buttons */}
              <div className="flex items-center gap-1.5 sm:gap-2">
                {/* Distance Button with Dropdown */}
              <div className="relative" ref={unitDropdownRef}>
                <Button
                  variant={sortBy === "distance" ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setSortBy("distance");
                    setHasUserSorted(true);
                  }}
                  className={`text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-2.5 pr-1 sm:pr-1.5 ${sortBy === "distance" ? "bg-primary text-primary-foreground" : ""}`}
                >
                  {t('sort_distance', 'Distance')}
                  <span className="text-[10px] sm:text-xs opacity-70 ml-0.5">({distanceUnit})</span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowUnitDropdown(!showUnitDropdown);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        setShowUnitDropdown(!showUnitDropdown);
                      }
                    }}
                    className="ml-0.5 p-0.5 rounded hover:bg-black/10 transition-colors cursor-pointer"
                  >
                    <ChevronDown className={`w-3 h-3 sm:w-3.5 sm:h-3.5 transition-transform ${showUnitDropdown ? 'rotate-180' : ''}`} />
                  </span>
                </Button>
                
                {/* Dropdown Menu */}
                {showUnitDropdown && (
                  <div className="absolute top-full left-0 mt-1 bg-white rounded-md shadow-lg border border-border py-1 z-30 min-w-[80px]">
                    <button
                      onClick={() => handleDistanceUnitChange('mi')}
                      className={`w-full px-3 py-1.5 text-xs sm:text-sm text-left hover:bg-muted transition-colors flex items-center justify-between ${
                        distanceUnit === 'mi' ? 'text-primary font-medium' : 'text-foreground'
                      }`}
                    >
                      Miles
                      {distanceUnit === 'mi' && <span className="text-primary">✓</span>}
                    </button>
                    <button
                      onClick={() => handleDistanceUnitChange('km')}
                      className={`w-full px-3 py-1.5 text-xs sm:text-sm text-left hover:bg-muted transition-colors flex items-center justify-between ${
                        distanceUnit === 'km' ? 'text-primary font-medium' : 'text-foreground'
                      }`}
                    >
                      Kilometers
                      {distanceUnit === 'km' && <span className="text-primary">✓</span>}
                    </button>
                  </div>
                )}
              </div>
              
              <Button
                variant={sortBy === "rating" ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setSortBy("rating");
                  setHasUserSorted(true);
                }}
                className={`text-xs sm:text-sm h-8 sm:h-9 px-2.5 sm:px-3 ${sortBy === "rating" ? "bg-primary text-primary-foreground" : ""}`}
              >
                {t('sort_rating', 'Rating')}
              </Button>
            </div>

            {/* View Toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setViewMode(viewMode === "list" ? "map" : "list")}
              className="gap-1.5 sm:gap-2 h-8 sm:h-9 px-2.5 sm:px-3 text-xs sm:text-sm"
            >
              {viewMode === "list" ? (
                <>
                  <MapIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">{t('map_view', 'Map')}</span>
                </>
              ) : (
                <>
                  <List className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">{t('list_view', 'List')}</span>
                </>
              )}
            </Button>
            </div>
          </div>
        </div>
      </div>

      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        {viewMode === "list" ? (
          /* List View */
          <div className="max-w-2xl mx-auto">
            <div className="px-3 py-3 sm:px-4 sm:py-4 space-y-2.5 sm:space-y-3">
            {sortedData.map((item) => (
              <SearchResultsCard
                key={item.id}
                id={item.id}
                name={item.name}
                address={item.address}
                website={item.website}
                phone={item.phone}
                distance={item.distance}
                distanceUnit={distanceUnit}
                rating={item.rating ? item.rating : t('no_ratings_available')}
                userRatingCount={item.userRatingCount ? item.userRatingCount : "0"}
                coordinates={item.coordinates}
              />
            ))}
            </div>
          </div>
        ) : (
          /* Map View */
          <div className="max-w-4xl mx-auto px-4 pt-3">
            <div className="flex flex-col md:flex-row h-[calc(100vh-14rem)] w-full rounded-lg overflow-hidden shadow-sm border border-border">
            {/* Sidebar - hidden on mobile */}
            <div className="hidden md:block w-80 flex-shrink-0 overflow-y-auto border-r border-border bg-white">
              {sortedData.map((item) => (
                <MapSearchCard
                  key={item.id}
                  place={item}
                  isActive={selectedPlace?.id === item.id}
                  distanceUnit={distanceUnit}
                  onSelect={(place) => {
                    setInfoWindowVisible(false);
                    setTimeout(() => {
                      setSelectedPlace(place);
                      setInfoWindowVisible(true);
                    }, 50);
                  }}
                />
              ))}
            </div>

            {/* Map */}
            <div className="flex-1 relative min-w-0">
              <Map
                style={{ width: "100%", height: "100%", maxWidth: "100%" }}
                defaultCenter={mapCenter || defaultCenter}
                defaultZoom={11}
                mapId="DEMO_MAP_ID"
                onLoad={(map) => setMapInstance(map)}
                onClick={handleMapClick}
                mapTypeControl={false}
                fullscreenControl={false}
                streetViewControl={false}
              >
                {/* User Location Marker */}
                {defaultCenter && (
                  <AdvancedMarker
                    position={defaultCenter}
                    gmpClickable={false}
                  >
                    <Pin
                      background="#dc2626"
                      glyphColor={"#fff"}
                      borderColor={"#991b1b"}
                    />
                  </AdvancedMarker>
                )}

                {validPlaces.map((place) => (
                  <AdvancedMarker
                    key={place.id}
                    position={{ lat: place.coordinates[1], lng: place.coordinates[0] }}
                    gmpClickable={true}
                    onClick={() => handleMarkerClick(place)}
                    ref={(el) => (markersRef.current[place.id] = el)}
                  >
                    <Pin
                      background={selectedPlace?.id === place.id ? "#2563eb" : "#3b82f6"}
                      glyphColor={"#fff"}
                      borderColor={"#1d4ed8"}
                    />
                  </AdvancedMarker>
                ))}

                {infoWindowVisible && selectedPlace && markersRef.current[selectedPlace.id] && (
                  <InfoWindow
                    anchor={markersRef.current[selectedPlace.id]}
                    onCloseClick={() => {
                      setSelectedPlace(null);
                      setInfoWindowVisible(false);
                    }}
                    headerDisabled={true}
                    maxWidth={isMobile ? 180 : 280}
                  >
                    <div style={{ 
                      padding: isMobile ? '6px' : '10px',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      boxSizing: 'border-box',
                      overflow: 'hidden',
                      width: '100%'
                    }}>
                      <h3 style={{ 
                        margin: '0 0 4px 0', 
                        fontSize: isMobile ? '12px' : '14px', 
                        fontWeight: '600', 
                        color: '#1f2937', 
                        lineHeight: '1.3',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                      }}>
                        {selectedPlace.name}
                      </h3>
                    
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '4px', 
                        marginBottom: '4px',
                        fontSize: isMobile ? '10px' : '11px',
                        color: '#6b7280'
                      }}>
                        <Star style={{ 
                          width: '10px', 
                          height: '10px', 
                          fill: '#facc15', 
                          color: '#facc15',
                          flexShrink: 0
                        }} />
                        <span style={{ fontWeight: '600', color: '#1f2937' }}>
                          {selectedPlace.rating || 'N/A'}
                        </span>
                        {selectedPlace.distance && (
                          <>
                            <span>·</span>
                            <span>{selectedPlace.distance.toFixed(1)} mi</span>
                          </>
                        )}
                      </div>
                      
                      <p style={{ 
                        margin: '0 0 6px 0', 
                        fontSize: isMobile ? '10px' : '12px', 
                        color: '#4b5563', 
                        lineHeight: '1.2',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: isMobile ? 2 : 3,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {selectedPlace.address}
                      </p>
                      
                      {selectedPlace.phone && (
                        <a
                          href={`tel:${selectedPlace.phone}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '3px',
                            marginBottom: '6px',
                            fontSize: isMobile ? '10px' : '11px',
                            color: '#7c9a6c',
                            textDecoration: 'none',
                            fontWeight: '500'
                          }}
                        >
                          <Phone style={{ width: '10px', height: '10px', flexShrink: 0 }} />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {selectedPlace.phone}
                          </span>
                        </a>
                      )}
                      
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: '4px'
                      }}>
                        <a
                          href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(selectedPlace.address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '3px',
                            padding: isMobile ? '5px 8px' : '6px 10px',
                            fontSize: isMobile ? '9px' : '11px',
                            fontWeight: '600',
                            backgroundColor: '#7c9a6c',
                            color: 'white',
                            borderRadius: '4px',
                            textDecoration: 'none',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        >
                          <Navigation style={{ width: '10px', height: '10px', flexShrink: 0 }} />
                          Directions
                        </a>
                        {selectedPlace.website && (
                          <a
                            href={selectedPlace.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '3px',
                              padding: isMobile ? '5px 8px' : '6px 10px',
                              fontSize: isMobile ? '9px' : '11px',
                              fontWeight: '500',
                              backgroundColor: '#f9fafb',
                              color: '#374151',
                              borderRadius: '4px',
                              textDecoration: 'none',
                              border: '1px solid #e5e7eb',
                              width: '100%',
                              boxSizing: 'border-box'
                            }}
                          >
                            <ExternalLink style={{ width: '10px', height: '10px', flexShrink: 0 }} />
                            Website
                          </a>
                        )}
                      </div>
                    </div>
                  </InfoWindow>
                )}
              </Map>
            </div>
          </div>
          </div>
        )}
      </APIProvider>
    </div>
  );
};

export default SearchResults;
